import "./index.css";
import { clsx } from "clsx";
import type { KeyboardEvent, MouseEvent } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslations } from "../../i18n";
import { Tag } from "../Tag";
import type { ISegmentProps } from "./types";

export const Segment = ({
  items,
  activeItem,
  onChange,
  className,
  variant = "hug",
}: ISegmentProps) => {
  const { rtl, t } = useTranslations();

  const selectionRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const activeIndex = useMemo(() => {
    if (!activeItem) return -1;
    return items.findIndex((item) => item.id === activeItem);
  }, [items, activeItem]);

  const getActiveButton = useCallback((): HTMLButtonElement | null => {
    if (activeIndex < 0) return null;
    const id = items[activeIndex]?.id;
    if (!id) return null;
    return buttonRefs.current.get(id) ?? null;
  }, [activeIndex, items]);

  const setSelection = useCallback(() => {
    const selection = selectionRef.current;
    const button = getActiveButton();
    if (!selection || !button) return;

    const container = containerRef.current;
    if (!container) return;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const width = buttonRect.width;
    // `left` is measured from the padding box, inside the container border.
    const left = buttonRect.left - containerRect.left - container.clientLeft;

    selection.style.left = `${left}px`;
    selection.style.width = `${width}px`;

    if (!selection.hasAttribute("data-initialized")) {
      selection.setAttribute("data-initialized", "true");
    }
  }, [getActiveButton]);

  useLayoutEffect(() => {
    setTimeout(setSelection);
  }, [activeIndex, items, setSelection]);

  useEffect(() => {
    const container = containerRef.current;
    const button = getActiveButton();
    if (!container) return;

    const ro = new ResizeObserver(() => {
      setSelection();
    });

    ro.observe(container);
    if (button) ro.observe(button);

    return () => {
      ro.disconnect();
    };
  }, [getActiveButton, setSelection]);

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const nextId = event.currentTarget.getAttribute("data-segment-id");
      const isDisabled =
        event.currentTarget.getAttribute("aria-disabled") === "true";
      if (!nextId || isDisabled) return;
      onChange(nextId);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const enabled = items.filter((i) => !i.disabled);
      if (enabled.length === 0) return;

      const focused = document.activeElement as HTMLElement | null;
      const focusedId = focused?.getAttribute?.("data-segment-id");
      if (!focusedId) return;

      const currentIndex = enabled.findIndex((i) => i.id === focusedId);
      if (currentIndex < 0) return;

      const moveFocus = (nextIndex: number) => {
        const nextItem = enabled[nextIndex];
        if (!nextItem) return;
        const btn = buttonRefs.current.get(nextItem.id);
        btn?.focus();
      };

      switch (event.key) {
        case "ArrowLeft": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex + 1) % enabled.length
            : (currentIndex - 1 + enabled.length) % enabled.length;
          moveFocus(next);
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex - 1 + enabled.length) % enabled.length
            : (currentIndex + 1) % enabled.length;
          moveFocus(next);
          break;
        }
        case "Home":
          event.preventDefault();
          moveFocus(0);
          break;
        case "End":
          event.preventDefault();
          moveFocus(enabled.length - 1);
          break;
        case "Enter":
        case " ": {
          event.preventDefault();
          const btn = buttonRefs.current.get(focusedId);
          const nextId = btn?.getAttribute("data-segment-id");
          const isDisabled = btn?.getAttribute("aria-disabled") === "true";
          if (nextId && !isDisabled) onChange(nextId);
          break;
        }
        default:
          break;
      }
    },
    [items, onChange, rtl],
  );

  const itemElements = useMemo(() => {
    return items.map((item) => {
      const isActive = item.id === activeItem;
      const btnClass = clsx(
        "nuco-segment__button",
        `nuco-segment__button--${variant}`,
        item.disabled && "nuco-segment__button--disabled",
        isActive && "nuco-segment__button--active",
        "nuco-text nuco-text--md nuco-text--bold",
        item.className,
      );

      return (
        <button
          {...(item.dataAttributes || {})}
          key={item.id}
          type="button"
          ref={(el) => {
            if (el) buttonRefs.current.set(item.id, el);
            else buttonRefs.current.delete(item.id);
          }}
          className={btnClass}
          data-segment-id={item.id}
          aria-pressed={isActive}
          aria-disabled={item.disabled ? "true" : "false"}
          tabIndex={isActive ? 0 : -1}
          onClick={handleButtonClick}
        >
          {item.text}
          {item.tagProps && (
            <Tag
              color="neutral"
              {...item.tagProps}
              className={clsx(item.tagProps?.className, "nuco-segment__tag")}
            />
          )}
        </button>
      );
    });
  }, [items, activeItem, variant, handleButtonClick]);

  const rootClass = clsx("nuco-segment", `nuco-segment--${variant}`, className);

  return (
    <div
      ref={containerRef}
      className={rootClass}
      role="group"
      aria-label={t("ui.segment.segmentControl")}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={selectionRef}
        aria-hidden="true"
        className="nuco-segment__indicator"
      />
      {itemElements}
    </div>
  );
};
